<template>
    <main>
        <!-- El contenedor debe tener altura fija: con responsive, Chart.js crece
             indefinidamente si el padre no la define. -->
        <div style="position: relative; height: 320px; width: 100%;">
            <canvas ref="lienzo"></canvas>
        </div>
        <p class="small text-muted mt-2 mb-0">
            Curva de alerta CLAP/OPS (percentil 10) según posición, paridad y estado de las membranas.
            El eje horizontal son las horas desde que la dilatación alcanzó 4 cm.
        </p>
    </main>
</template>

<script>
import Chart from 'chart.js/auto';

export default {
    name: 'ChartPartograma',
    props: {
        // Patrón CLAP seleccionado: { labels: [min 4→5, 5→6, …], posicion, paridad, membranas }
        patron: { type: Object, default: null },
        // Controles registrados del partograma
        controles: { type: Array, default: () => [] },
    },
    data() {
        return { instancia: null };
    },
    computed: {
        /** Curva de alerta: minutos acumulados por centímetro, desde 4 cm. */
        curvaAlerta() {
            if (!this.patron || !Array.isArray(this.patron.labels)) return [];

            let acumulado = 0;
            const puntos = [{ x: 0, y: 4 }];

            this.patron.labels.forEach((minutos, i) => {
                acumulado += Number(minutos) || 0;
                puntos.push({ x: Number((acumulado / 60).toFixed(2)), y: 5 + i });
            });

            return puntos;
        },
        /** Hora en que la dilatación llegó a 4 cm: el cero del eje horizontal. */
        horaCero() {
            const conDilatacion = this.controles
                .filter(c => c.dilatacion !== null && c.dilatacion !== undefined && c.fecha)
                .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

            if (!conDilatacion.length) return null;

            const inicio = conDilatacion.find(c => Number(c.dilatacion) >= 4) || conDilatacion[0];
            return new Date(inicio.fecha).getTime();
        },
        /** Dilatación registrada, en horas desde que se alcanzaron los 4 cm. */
        curvaReal() {
            const t0 = this.horaCero;
            if (t0 === null) return [];

            return this.controles
                .filter(c => c.dilatacion !== null && c.dilatacion !== undefined && c.fecha)
                .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
                .map(c => ({
                    x: Number(((new Date(c.fecha).getTime() - t0) / 3600000).toFixed(2)),
                    y: Number(c.dilatacion),
                }));
        },
        /** Descenso en planos de Hodge (I..IV) sobre el mismo eje de horas. */
        curvaDescenso() {
            const planos = { I: 1, II: 2, III: 3, IV: 4 };
            const t0 = this.horaCero;
            if (t0 === null) return [];

            return this.controles
                .filter(c => planos[c.descenso] && c.fecha)
                .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
                .map(c => ({
                    x: Number(((new Date(c.fecha).getTime() - t0) / 3600000).toFixed(2)),
                    y: planos[c.descenso],
                }));
        },
        horasMaximas() {
            const xs = [...this.curvaAlerta, ...this.curvaReal].map(p => p.x);
            return Math.max(8, Math.ceil(Math.max(0, ...xs)) + 1);
        },
    },
    watch: {
        patron: 'dibujar',
        controles: 'dibujar',
    },
    mounted() {
        this.$nextTick(this.dibujar);
    },
    beforeDestroy() {
        if (this.instancia) this.instancia.destroy();
    },
    methods: {
        dibujar() {
            if (this.instancia) this.instancia.destroy();

            this.instancia = new Chart(this.$refs.lienzo, {
                type: 'line',
                data: {
                    datasets: [
                        {
                            label: 'Curva de alerta (CLAP)',
                            data: this.curvaAlerta,
                            borderColor: '#dc3545',
                            backgroundColor: '#dc3545',
                            borderDash: [6, 4],
                            borderWidth: 2,
                            pointRadius: 0,
                            fill: false,
                            tension: 0,
                        },
                        {
                            label: 'Dilatación registrada',
                            data: this.curvaReal,
                            borderColor: '#0061f2',
                            backgroundColor: '#0061f2',
                            borderWidth: 2,
                            pointRadius: 4,
                            fill: false,
                            tension: 0,
                        },
                        {
                            label: 'Descenso (planos de Hodge)',
                            data: this.curvaDescenso,
                            borderColor: '#00ac69',
                            backgroundColor: '#00ac69',
                            borderWidth: 2,
                            pointRadius: 4,
                            fill: false,
                            tension: 0,
                            yAxisID: 'y1',
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { position: 'bottom' },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const valor = ctx.dataset.yAxisID === 'y1'
                                        ? `plano ${{ 1: 'I', 2: 'II', 3: 'III', 4: 'IV' }[ctx.parsed.y]}`
                                        : `${ctx.parsed.y} cm`;
                                    return `${ctx.dataset.label}: ${valor} a las ${ctx.parsed.x} h`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            type: 'linear',
                            min: 0,
                            max: this.horasMaximas,
                            title: { display: true, text: 'Horas desde 4 cm' },
                            ticks: { stepSize: 1 },
                        },
                        y: {
                            min: 4,
                            max: 10,
                            title: { display: true, text: 'Dilatación (cm)' },
                            ticks: { stepSize: 1 },
                        },
                        // El descenso baja mientras la dilatación sube: el eje va
                        // invertido para que el plano I quede arriba.
                        y1: {
                            position: 'right',
                            min: 1,
                            max: 4,
                            reverse: true,
                            title: { display: true, text: 'Plano de Hodge' },
                            grid: { drawOnChartArea: false },
                            ticks: {
                                stepSize: 1,
                                callback: (v) => ({ 1: 'I', 2: 'II', 3: 'III', 4: 'IV' }[v] || ''),
                            },
                        },
                    },
                },
            });
        },
    },
};
</script>
